import React, { createContext, useContext, useEffect, useState, useCallback, type JSX } from 'react';
import { Purchases, type CustomerInfo, type Package, type Offerings } from '@revenuecat/purchases-js';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { Patient } from '@medplum/fhirtypes';

const REVENUECAT_API_KEY = import.meta.env.VITE_REVENUECAT_API_KEY;
const SUBSCRIPTION_EXTENSION_URL = 'https://progressnotes.app/fhir/StructureDefinition/subscription-status';
const SUBSCRIPTION_TIER_URL = 'https://progressnotes.app/fhir/StructureDefinition/subscription-tier';
const SUBSCRIPTION_BILLING_CYCLE_URL = 'https://progressnotes.app/fhir/StructureDefinition/subscription-billing-cycle';
const TRIAL_END_DATE_URL = 'https://progressnotes.app/fhir/StructureDefinition/trial-end-date';

interface RevenueCatContextType {
  isLoading: boolean;
  isInitialized: boolean;
  customerInfo: CustomerInfo | null;
  offerings: Offerings | null;
  isPremium: boolean;
  isBasic: boolean;
  isFree: boolean;
  isTrialActive: boolean;
  trialDaysRemaining: number;
  availablePackages: Package[];
  purchase: (pkg: Package) => Promise<{ success: boolean; error?: string }>;
  refreshCustomerInfo: () => Promise<void>;
  error: string | null;
}

const RevenueCatContext = createContext<RevenueCatContextType | undefined>(undefined);

export function RevenueCatProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Patient;
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo | null>(null);
  const [offerings, setOfferings] = useState<Offerings | null>(null);
  const [availablePackages, setAvailablePackages] = useState<Package[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Initialize RevenueCat when profile is available
  useEffect(() => {
    async function initialize(): Promise<void> {
      if (!profile?.id || !REVENUECAT_API_KEY) {
        console.log('[RevenueCat] Missing profile ID or API key, skipping initialization');
        setIsLoading(false);
        return;
      }

      try {
        console.log('[RevenueCat] Initializing with patient ID:', profile.id);

        // Configure RevenueCat with Patient ID as the app user ID
        // This ensures cross-platform sync works with Cora mobile app
        Purchases.configure(REVENUECAT_API_KEY, profile.id);

        setIsInitialized(true);

        // Get current customer info
        const info = await Purchases.getSharedInstance().getCustomerInfo();
        console.log('[RevenueCat] Customer info retrieved:', {
          originalAppUserId: info.originalAppUserId,
          activeEntitlements: Object.keys(info.entitlements.active),
        });
        setCustomerInfo(info);

        // Get available offerings
        const offeringsResult = await Purchases.getSharedInstance().getOfferings();
        console.log('[RevenueCat] Offerings retrieved:', {
          current: offeringsResult.current?.identifier,
          availablePackages: offeringsResult.current?.availablePackages.map((p: Package) => p.identifier),
        });
        setOfferings(offeringsResult);

        if (offeringsResult.current) {
          setAvailablePackages(offeringsResult.current.availablePackages);
        }

        // Sync subscription status to FHIR for offline/fallback access
        await syncToFHIR(info);

      } catch (err) {
        console.error('[RevenueCat] Initialization error:', err);
        setError(err instanceof Error ? err.message : 'Failed to initialize RevenueCat');

        // Fall back to FHIR extension data if RevenueCat fails
        await loadFromFHIR();
      } finally {
        setIsLoading(false);
      }
    }

    initialize();
  }, [profile?.id]);

  // Derived state from customerInfo
  // Note: RevenueCat only has 'premium' (Voice Connect) - no 'basic' tier
  const isPremium = customerInfo?.entitlements.active['premium'] !== undefined;
  const isBasic = false; // No basic tier in RevenueCat
  const isFree = !isPremium;

  // Trial detection
  const premiumEntitlement = customerInfo?.entitlements.active['premium'];

  const isTrialActive = premiumEntitlement?.periodType === 'trial';
  const trialDaysRemaining = isTrialActive && premiumEntitlement?.expirationDate
    ? Math.max(0, Math.ceil((new Date(premiumEntitlement.expirationDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : 0;

  // Purchase function - passes customer email for receipts
  const purchase = useCallback(async (pkg: Package): Promise<{ success: boolean; error?: string }> => {
    if (!isInitialized) {
      return { success: false, error: 'RevenueCat not initialized' };
    }

    try {
      // Get customer email from OAuth userinfo endpoint (returns User.email)
      let customerEmail: string | undefined;

      try {
        const userInfo = await medplum.get('oauth2/userinfo');
        customerEmail = userInfo?.email;
        console.log('[RevenueCat] Got email from userinfo:', customerEmail);
      } catch (e) {
        console.log('[RevenueCat] Could not get email from userinfo:', e);
        // Fallback to Patient.telecom
        customerEmail = profile?.telecom?.find(t => t.system === 'email')?.value;
      }

      console.log('[RevenueCat] Starting purchase for package:', pkg.identifier);
      console.log('[RevenueCat] Customer email:', customerEmail || '(will be collected during checkout)');

      // Pass email to purchase so receipts go to the right place
      const { customerInfo: newInfo } = await Purchases.getSharedInstance().purchasePackage(
        pkg,
        customerEmail
      );

      console.log('[RevenueCat] Purchase successful:', {
        activeEntitlements: Object.keys(newInfo.entitlements.active),
      });

      setCustomerInfo(newInfo);
      await syncToFHIR(newInfo);

      return { success: true };
    } catch (err) {
      console.error('[RevenueCat] Purchase error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Purchase failed';
      return { success: false, error: errorMessage };
    }
  }, [isInitialized, profile?.telecom]);

  // Refresh customer info
  const refreshCustomerInfo = useCallback(async (): Promise<void> => {
    if (!isInitialized) return;

    try {
      const info = await Purchases.getSharedInstance().getCustomerInfo();
      setCustomerInfo(info);
      await syncToFHIR(info);
    } catch (err) {
      console.error('[RevenueCat] Refresh error:', err);
    }
  }, [isInitialized]);

  // Sync subscription status to FHIR Patient extensions
  // Updates both JSON format (for Cora compatibility) and simple string extensions (for useSubscriptionFeatures)
  const syncToFHIR = async (info: CustomerInfo): Promise<void> => {
    if (!profile?.id) return;

    try {
      const patient = await medplum.readResource('Patient', profile.id);

      // Note: RevenueCat only has 'premium' (Voice Connect) - no 'basic' tier
      const hasPremium = info.entitlements.active['premium'] !== undefined;
      const premiumEntitlement = info.entitlements.active['premium'];
      const isTrialPeriod = premiumEntitlement?.periodType === 'trial';

      // Determine subscription status string for useSubscriptionFeatures
      let subscriptionStatus: string;
      if (isTrialPeriod) {
        subscriptionStatus = 'trial';
      } else if (hasPremium) {
        subscriptionStatus = 'active';
      } else {
        subscriptionStatus = 'free';
      }

      // Determine tier string (only premium or free - no basic in RevenueCat)
      const subscriptionTier = hasPremium ? 'premium' : 'free';

      // Determine billing cycle from product identifier (if available)
      const productId = premiumEntitlement?.productIdentifier || '';
      const billingCycle = productId.toLowerCase().includes('annual') ? 'annual' : 'monthly';

      // JSON data for Cora compatibility
      const subscriptionData = {
        customerId: info.originalAppUserId,
        isPremium: hasPremium,
        isBasic: false, // No basic tier in RevenueCat
        expirationDate: premiumEntitlement?.expirationDate,
        productIdentifier: premiumEntitlement?.productIdentifier,
        periodType: premiumEntitlement?.periodType,
      };

      // Filter out all subscription-related extensions to replace them
      const extensionUrlsToReplace = [
        SUBSCRIPTION_EXTENSION_URL,
        SUBSCRIPTION_TIER_URL,
        SUBSCRIPTION_BILLING_CYCLE_URL,
        TRIAL_END_DATE_URL,
      ];
      const filteredExtensions = (patient.extension || []).filter(
        e => !extensionUrlsToReplace.includes(e.url || '')
      );

      // Build new extensions array
      const newExtensions = [
        ...filteredExtensions,
        // Simple string status for useSubscriptionFeatures
        {
          url: SUBSCRIPTION_EXTENSION_URL,
          valueString: subscriptionStatus,
        },
        // Tier extension
        {
          url: SUBSCRIPTION_TIER_URL,
          valueString: subscriptionTier,
        },
        // Billing cycle
        {
          url: SUBSCRIPTION_BILLING_CYCLE_URL,
          valueString: billingCycle,
        },
      ];

      // Add trial end date if in trial
      if (isTrialPeriod && premiumEntitlement?.expirationDate) {
        // Convert Date to ISO string if needed
        const expirationDateStr = premiumEntitlement.expirationDate instanceof Date
          ? premiumEntitlement.expirationDate.toISOString()
          : String(premiumEntitlement.expirationDate);
        newExtensions.push({
          url: TRIAL_END_DATE_URL,
          valueDateTime: expirationDateStr,
        });
      }

      const updatedPatient: Patient = {
        ...patient,
        extension: newExtensions,
      };

      await medplum.updateResource(updatedPatient);
      console.log('[RevenueCat] Synced to FHIR:', { subscriptionStatus, subscriptionTier, billingCycle, subscriptionData });
    } catch (err) {
      console.error('[RevenueCat] FHIR sync error:', err);
    }
  };

  // Load from FHIR as fallback when RevenueCat is unavailable
  const loadFromFHIR = async (): Promise<void> => {
    if (!profile?.extension) return;

    const ext = profile.extension.find(e => e.url === SUBSCRIPTION_EXTENSION_URL);
    if (ext?.valueString) {
      try {
        const data = JSON.parse(ext.valueString);
        console.log('[RevenueCat] Loaded from FHIR fallback:', data);
        // Note: We can't fully reconstruct CustomerInfo from FHIR,
        // but the derived states (isPremium, etc.) will work from this data
      } catch (err) {
        console.error('[RevenueCat] FHIR parse error:', err);
      }
    }
  };

  return (
    <RevenueCatContext.Provider value={{
      isLoading,
      isInitialized,
      customerInfo,
      offerings,
      isPremium,
      isBasic,
      isFree,
      isTrialActive,
      trialDaysRemaining,
      availablePackages,
      purchase,
      refreshCustomerInfo,
      error,
    }}>
      {children}
    </RevenueCatContext.Provider>
  );
}

export function useRevenueCat(): RevenueCatContextType {
  const context = useContext(RevenueCatContext);
  if (!context) {
    throw new Error('useRevenueCat must be used within RevenueCatProvider');
  }
  return context;
}

// Helper to check if RevenueCat is enabled
export function isRevenueCatEnabled(): boolean {
  return !!import.meta.env.VITE_REVENUECAT_API_KEY;
}
